// Per-user book for a single market on the game-details page.
// Rows are the downline as a collapsible tree (staff → … → end user), columns
// are the market's runners. Staff rows are upline-side (sign flipped), end
// users their own P&L — see getUserBook in reportsController.
import { useState, useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { getUserBook } from '../services/api'
import useModalEnter from '../hooks/useModalEnter'
import LoaderOverlay from './LoaderOverlay'

/* Reference `getBookData(mid, gtype)` (admin bundle):
 *
 *   store.state.isLoading = true                    // loader FIRST
 *   callApi('game/allbook', {gid, mid, gtype}).then(r => {
 *     …build the book…
 *     store.state.isLoading = false                 // loader off…
 *     allbookModal.show()                           // …then the modal
 *   }).catch(() => { store.state.isLoading = false })   // failure → NO modal
 *
 * So the modal never appears before its data. Ours opened instantly and said
 * "Loading…" and then "No records found" inside an empty table (user's screen
 * recording, 2026-09-22): "pehle loader aana chahiye, andar koi user hi nahi
 * hai to modal khulna hi nahi chahiye". Hence the three render states below —
 * loader, nothing, or the filled modal — and the empty case closes itself so
 * the next click starts clean.
 */
// Reference book cells: plain black numbers, "-" on a loss, no "+" and no
// brackets on a profit, at most 2 decimals with trailing zeros dropped.
const formatBook = (value) => {
  if (value == null) return '-'
  const n = Number(value)
  return Number.isFinite(n) ? String(Number(n.toFixed(2))) : '-'
}

// Sibling order as on the reference (agvishu before agvishu1)
const sortNodes = (nodes) => [...nodes].sort((a, b) =>
  String(a.username).localeCompare(String(b.username), undefined, { sensitivity: 'base', numeric: true }))

// One hierarchy node and, when opened, its children. Every level starts
// collapsed like the reference ([+] newcheck1 → [-] newcheck1, [+] agvishu …);
// end users carry no toggle. The reference does not indent by level: every
// name sits 20px in from "User Name" (its `.mtree li td:first-child`).
function BookRows({ node, runners }) {
  const [open, setOpen] = useState(false)
  const isFolder = Array.isArray(node.children) && node.children.length > 0
  return (
    <>
      <tr>
        <td
          style={{ paddingLeft: '20px', ...(isFolder ? { cursor: 'pointer' } : null) }}
          onClick={isFolder ? () => setOpen((o) => !o) : undefined}
        >
          {isFolder ? `[${open ? '-' : '+'}] ` : ''}{node.username}
        </td>
        {runners.map((runner, i) => (
          <td key={runner} className="text-right" style={{ color: '#000' }}>
            {formatBook(node.values?.[i])}
          </td>
        ))}
      </tr>
      {isFolder && open && sortNodes(node.children).map((child) => (
        <BookRows key={child.id} node={child} runners={runners} />
      ))}
    </>
  )
}

// The API lists runners in first-bet order; the reference shows them A→Z,
// not in match order ("Cuiaba v Avai" → Avai, Cuiaba, The Draw). Re-sort the
// columns and move every node's values with them.
const orderBook = (runners, tree) => {
  const idx = runners.map((_, i) => i).sort((a, b) =>
    String(runners[a]).localeCompare(String(runners[b]), undefined, { sensitivity: 'base', numeric: true }))
  const reorder = (node) => ({
    ...node,
    values: idx.map((i) => node.values?.[i] ?? null),
    ...(node.children ? { children: node.children.map(reorder) } : null),
  })
  return { runners: idx.map((i) => runners[i]), tree: tree.map(reorder) }
}

function UserBookModal({ show, onHide, eventId, gameType }) {
  const [runners, setRunners] = useState([])
  const [rows, setRows] = useState([])
  // 'idle' before the first fetch of an opening, 'loading' while it runs,
  // 'ready' once rows/runners hold that fetch's answer
  const [status, setStatus] = useState('idle')

  const fetchBook = useCallback(async () => {
    if (!eventId) return
    setStatus('loading')
    try {
      const response = await getUserBook({ eventid: eventId, game_type: gameType })
      if (response?.success) {
        const book = orderBook(response.runners || [], response.tree || [])
        setRunners(book.runners)
        setRows(book.tree)
      } else {
        setRunners([])
        setRows([])
      }
    } catch (err) {
      console.error('Error fetching user book:', err)
      setRunners([])
      setRows([])
    } finally {
      setStatus('ready')
    }
  }, [eventId, gameType])

  useEffect(() => {
    if (show) fetchBook()
    else setStatus('idle')
  }, [show, fetchBook])

  const hasBook = rows.length > 0
  // nothing to show → never opened, so hand `show` back to the page
  useEffect(() => {
    if (show && status === 'ready' && !hasBook) onHide?.()
  }, [show, status, hasBook, onHide])

  // the open animation starts when the modal actually mounts, not on the click
  const { modalClass, backdropClass, onBackdropClick } = useModalEnter(onHide, show && status === 'ready' && hasBook)

  if (!show) return null
  if (status !== 'ready') return createPortal(<LoaderOverlay />, document.body)
  if (!hasBook) return null

  /* Portaled to <body>, like BootstrapVue's own `b-modal`
     (`…___BV_modal_outer_` is a direct child of body in the reference capture).
     In place it would sit inside `.detail-page-container`, whose
     `font-family: "Noto Sans"` (style.css:732) would beat the `body`
     rule — the reference renders its modals in Poppins. */
  return createPortal(
    <div style={{ position: 'absolute', zIndex: 1040 }}>
      <div role="dialog" className={modalClass} aria-modal="true" onClick={onBackdropClick} style={{ display: 'block' }}>
        <div className="modal-dialog modal-xl">
          <span tabIndex="0"></span>
          <div tabIndex="-1" className="modal-content">
            <header className="modal-header">
              <h5 className="modal-title">User Book</h5>
              <button type="button" aria-label="Close" className="close" onClick={onHide}>×</button>
            </header>
            <div className="modal-body">
              <div className="user-book">
                <table className="table mb-0">
                  <thead>
                    <tr>
                      <th>User Name</th>
                      {runners.map((runner) => (
                        <th key={runner} className="text-right">{runner}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {/* the modal only mounts with rows in hand, so no
                        "Loading…" / "No records found" state is reachable */}
                    {sortNodes(rows).map((node) => (
                      <BookRows key={node.id} node={node} runners={runners} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
          <span tabIndex="0"></span>
        </div>
      </div>
      <div className={backdropClass}></div>
    </div>,
    document.body,
  )
}

export default UserBookModal
